import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { PageHeader } from '@/components/efs/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ApiClientError } from '@/lib/api-client';
import { useCurrentUser } from '@/lib/auth-context';
import { applyApiFieldErrors } from '@/lib/form-errors';
import { meetsPasswordPolicy } from '@/lib/password-policy';

import { changeOwnPassword, type ChangeOwnPasswordInput } from './api';

const NS = 'settings';

/**
 * 我的系統管理: the only self-service account page. It changes the signed-in
 * user's own password and nothing else — own permissions are neither shown
 * nor editable here, and the account-management page refuses the caller's own
 * row. Same for every account, system_admin included.
 */
const MyAccountPage = () => {
  const { t } = useTranslation(NS);
  const me = useCurrentUser();
  const [done, setDone] = useState(false);
  const change = useMutation<void, ApiClientError, ChangeOwnPasswordInput>({
    mutationFn: changeOwnPassword,
  });

  // Same rule as the backend's validate_password_strength, with localized text.
  const schema = useMemo(() => {
    const policy = t('efs.settings.my_account.errors.password_policy');
    return z
      .object({
        current_password: z.string().min(1, t('efs.settings.my_account.errors.current_required')),
        new_password: z.string().refine(meetsPasswordPolicy, policy),
        confirm_password: z.string(),
      })
      .refine((v) => v.new_password === v.confirm_password, {
        path: ['confirm_password'],
        message: t('efs.settings.my_account.errors.confirm_mismatch'),
      });
  }, [t]);
  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  const simulating = !!me?.simulatedBy;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors();
    setDone(false);
    try {
      await change.mutateAsync({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      form.reset({ current_password: '', new_password: '', confirm_password: '' });
      setDone(true);
    } catch (error) {
      if (error instanceof ApiClientError && error.code === 'CURRENT_PASSWORD_INCORRECT') {
        form.setError('current_password', {
          type: error.code,
          message: t('efs.settings.my_account.errors.current_incorrect'),
        });
        return;
      }
      applyApiFieldErrors(form, error, ['new_password']);
    }
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.settings.my_account.title')}
        subtitle={t('efs.settings.my_account.subtitle')}
      />
      <section className="max-w-md space-y-4 rounded-md border border-efs-border bg-efs-bg-panel p-4">
        <div>
          <h2 className="text-sm font-semibold text-efs-text-primary">
            {t('efs.settings.my_account.password_section')}
          </h2>
          <p className="text-xs text-efs-text-muted">{me?.email}</p>
        </div>

        {simulating ? (
          <p className="rounded-md bg-efs-bg-subtle px-3 py-2 text-sm text-efs-text-muted">
            {t('efs.settings.my_account.messages.simulating')}
          </p>
        ) : (
          <Form {...form}>
            <form onSubmit={submit} className="space-y-4" noValidate>
              <FormField
                control={form.control}
                name="current_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.settings.my_account.fields.current_password')}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="new_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.settings.my_account.fields.new_password')}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirm_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.settings.my_account.fields.confirm_password')}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.formState.errors.root?.message ? (
                <p className="text-sm font-medium text-destructive">
                  {String(form.formState.errors.root.message)}
                </p>
              ) : null}
              {done ? (
                <p role="status" className="text-sm font-medium text-efs-success">
                  {t('efs.settings.my_account.messages.changed')}
                </p>
              ) : null}

              <Button type="submit" disabled={change.isPending}>
                {change.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('efs.settings.my_account.actions.change_password')}
              </Button>
            </form>
          </Form>
        )}
      </section>
    </div>
  );
};

export default MyAccountPage;
